import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  SkeletonRows,
  StatusBadge,
  cn,
  useToast,
} from '@ambernord/ui';
import { ArrowRight, Handshake } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { formatDate, formatNumber, productName } from '../../lib/format.js';
import { RequestQuotePanel } from './RequestQuotePanel.js';
import {
  type CustomerOfferDetail,
  type CustomerProfile,
  type OfferRow,
  type ProductItem,
  byNewest,
  errorKey,
} from './types.js';

/**
 * Klienta skats (§4.9 — API jau projicē): pieprasījumu saraksts + saņemtā piedāvājuma
 * kategoriju kopsavilkums ar LIELO gala cenu un Approve pogu. Margin/piegādātāja
 * lauki nekad nenāk no API un šeit netiek izgudroti.
 */
export function CustomerOffers() {
  const { t, i18n } = useTranslation('offers');
  const { t: tStatus } = useTranslation('statuses');
  const { toast } = useToast();
  const locale = i18n.language;

  const [offers, setOffers] = useState<OfferRow[] | null>(null);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, CustomerOfferDetail>>({});
  const [panelOpen, setPanelOpen] = useState(false);
  const [approving, setApproving] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [offersRes, productsRes, profileRes] = await Promise.all([
      api.get<{ items?: OfferRow[]; error?: string }>('/api/offers'),
      api.get<{ items?: ProductItem[] }>('/api/products'),
      api.get<CustomerProfile>('/api/customers/me'),
    ]);
    if (!offersRes.ok) {
      setError(t('errors.loadFailed'));
      return;
    }
    setError(null);
    setOffers([...(offersRes.data.items ?? [])].sort(byNewest));
    if (productsRes.ok) setProducts(productsRes.data.items ?? []);
    if (profileRes.ok) {
      setProfile(profileRes.data);
      setProfileMissing(false);
    } else {
      // 404 = klienta tenantam vēl nav piesaistīta customers rinda → RFQ nav iespējams.
      setProfileMissing(profileRes.status === 404);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Saņemtajiem (offer_sent) piedāvājumiem ielādē kategoriju kopsavilkumu automātiski.
  useEffect(() => {
    if (!offers) return;
    const pending = offers.filter((o) => o.status === 'offer_sent' && !details[o.id]);
    if (pending.length === 0) return;
    let active = true;
    void (async () => {
      const loaded = await Promise.all(
        pending.map(async (o) => {
          const res = await api.get<CustomerOfferDetail>(`/api/offers/${o.id}`);
          return res.ok ? ([o.id, res.data] as const) : null;
        }),
      );
      if (!active) return;
      setDetails((prev) => {
        const next = { ...prev };
        for (const entry of loaded) {
          if (entry) next[entry[0]] = entry[1];
        }
        return next;
      });
    })();
    return () => {
      active = false;
    };
  }, [offers, details]);

  const productMap = new Map(products.map((p) => [p.id, p]));

  async function approve(offerId: string) {
    setApproving(offerId);
    try {
      const res = await api.post<{ placedOrderId?: string; error?: string }>(
        `/api/offers/${offerId}/approve`,
      );
      if (!res.ok) {
        toast(t(errorKey(res.data.error)), 'error');
        return;
      }
      toast(t('toasts.orderPlaced'), 'success');
      await load();
    } finally {
      setApproving(null);
    }
  }

  const requestButton = (
    <Button size="sm" onClick={() => setPanelOpen(true)}>
      {t('actions.requestQuote')}
    </Button>
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('intro.customer')}</p>
        {requestButton}
      </div>

      {error ? (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : offers === null ? (
        <SkeletonRows rows={5} />
      ) : offers.length === 0 ? (
        <EmptyState
          icon={<Handshake className="h-6 w-6" aria-hidden />}
          title={t('empty.customerTitle')}
          hint={t('empty.customerHint')}
          action={requestButton}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {offers.map((offer) => {
            const sent = offer.status === 'offer_sent';
            const detail = details[offer.id];
            return (
              <Card
                key={offer.id}
                className={cn(sent && 'ring-2 ring-amber-400 dark:ring-amber-500')}
              >
                <CardHeader
                  title={
                    <span className="flex flex-wrap items-center gap-2">
                      {offer.offerNo}
                      <StatusBadge status={offer.status} label={tStatus(offer.status)} />
                      {sent ? <Badge tone="amber">{t('labels.offerReceived')}</Badge> : null}
                    </span>
                  }
                  subtitle={`${t('labels.created')}: ${formatDate(offer.createdAt, locale)} · ${t('labels.market')}: ${offer.market.toUpperCase()}`}
                />
                <CardBody>
                  {sent ? (
                    detail ? (
                      <div className="flex flex-col gap-4">
                        {detail.lines.length > 0 ? (
                          <ul className="text-sm text-slate-600 dark:text-slate-300">
                            {detail.lines.map((l) => (
                              <li key={l.id}>
                                {productName(
                                  productMap.get(l.productId)?.namesI18n,
                                  locale,
                                  l.productId,
                                )}{' '}
                                — {formatNumber(l.qty, locale)} {l.unit}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        <dl className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800">
                          {detail.costSummary.map((row) => (
                            <div
                              key={row.label}
                              className="flex items-center justify-between px-3 py-2"
                            >
                              <dt className="text-slate-500 dark:text-slate-400">
                                {t(`summary.${row.label}`, { defaultValue: row.label })}
                              </dt>
                              <dd className="tabular-nums font-medium">
                                {formatNumber(row.amount, locale)} {detail.currency}
                              </dd>
                            </div>
                          ))}
                        </dl>
                        <div className="flex flex-wrap items-end justify-between gap-3">
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                              {t('labels.grandTotal')}
                            </p>
                            <p className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                              {formatNumber(detail.total, locale)}{' '}
                              <span className="text-lg font-semibold text-slate-500 dark:text-slate-400">
                                {detail.currency}
                              </span>
                            </p>
                            {offer.validUntil ? (
                              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                {t('labels.validUntil')}: {formatDate(offer.validUntil, locale)}
                              </p>
                            ) : null}
                          </div>
                          <Button
                            disabled={approving === offer.id}
                            onClick={() => void approve(offer.id)}
                          >
                            {approving === offer.id ? t('loading') : t('actions.approve')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <SkeletonRows rows={3} />
                    )
                  ) : offer.status === 'offer_approved' ? (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        {t('labels.approvedAt')}: {formatDate(offer.approvedAt, locale)}
                        {offer.total ? (
                          <>
                            {' · '}
                            <span className="font-semibold text-slate-900 dark:text-white">
                              {formatNumber(offer.total, locale)} {offer.currency ?? 'EUR'}
                            </span>
                          </>
                        ) : null}
                      </p>
                      <Link to="/orders">
                        <Button variant="secondary" size="sm">
                          {t('actions.goToOrders')}
                          <ArrowRight className="h-4 w-4" aria-hidden />
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {offer.notes
                        ? `${t('labels.notes')}: ${offer.notes}`
                        : t('labels.waitingPlatform')}
                    </p>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <RequestQuotePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onCreated={() => void load()}
        products={products}
        profile={profile}
        profileMissing={profileMissing}
      />
    </div>
  );
}
